/**
 * NodeOrbit — orbital visualization with BURST entry + steady fires.
 *
 * On entry: Dashboard sends burstFires — 3-5 rapid sequential fires
 * showing accumulated profit. Multiple fires visible simultaneously
 * (activeFires array, each auto-expires after 2s).
 *
 * Steady mode: Server SSE fires every 45-90s. Each fire shows a
 * meaningful amount (not dailyIncome/10800 — fewer fires = bigger).
 *
 * Fallback: If no SSE and no burst, selfDrive picks random nodes
 * every ~60s.
 *
 * Animations are pure CSS keyframes — 60fps on mid-range phones.
 */
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { BUSINESSES } from '@/data/businesses';
import { currentTick, syncedRandom, syncedRandomInt } from '@/lib/sharedRandom';
import { useUISounds } from '@/hooks/useUISounds';

export interface BurstFire {
  nodeIdx: number;
  amount: number;
}

interface NodeOrbitProps {
  totalLps: number;
  className?: string;
  /** Server SSE fire — only nodeIdx matters, amount from dripAmount. */
  serverFire?: { nodeIdx: number; amount: number; ts: number } | null;
  /** Real per-tick income for steady fires (from Dashboard). */
  dripAmount?: number;
  /** Entry burst: array of fires to play rapidly on mount.
   *  Set ONCE by Dashboard. Each fire renders concurrently. */
  burstFires?: BurstFire[] | null;
  /** Called after fire → core → balance tick sequence. */
  onFire?: (amount: number, businessIdx: number) => void;
  onTapNode?: (businessIdx: number) => void;
  onTapCenter?: () => void;
}

const businesses = BUSINESSES;

interface FireEvent {
  idx: number;
  amount: number;
  key: number;
}

const RADIUS = 100;
const CENTER = 150;
const INTRO_DURATION_MS = 2700;
const INTRO_START_DELAY_S = 0;

// Время жизни fire-элемента (ring + float) — чуть дольше CSS анимации
const FIRE_LIFETIME_MS = 2000;

interface ConnectionEvent {
  from: number;
  to: number;
  key: number;
}

function NodeOrbitImpl({
  totalLps, className, serverFire, dripAmount,
  burstFires, onFire, onTapNode, onTapCenter,
}: NodeOrbitProps) {
  const sounds = useUISounds();
  // Множественные concurrent fires (для burst)
  const [activeFires, setActiveFires] = useState<FireEvent[]>([]);
  const [coreKey, setCoreKey] = useState(0);
  const [phase, setPhase] = useState<'intro' | 'live'>('intro');
  const [connection, setConnection] = useState<ConnectionEvent | null>(null);
  const fireKeyRef = useRef(0);
  const connectionKeyRef = useRef(0);

  const onFireRef = useRef(onFire);
  useEffect(() => { onFireRef.current = onFire; });

  const serverFireRef = useRef(serverFire);
  useEffect(() => { serverFireRef.current = serverFire; });

  const dripRef = useRef(dripAmount ?? 0);
  useEffect(() => { dripRef.current = dripAmount ?? 0; });

  // Burst tracking — обрабатываем один раз
  const burstProcessedRef = useRef(false);

  // Добавить fire-анимацию на ноду (с авто-удалением)
  const addFire = useCallback((idx: number, amount: number) => {
    fireKeyRef.current += 1;
    const key = fireKeyRef.current;
    const fire: FireEvent = { idx, amount, key };
    setActiveFires(prev => [...prev, fire]);
    // Авто-удаление после завершения CSS (1.6s float + запас)
    window.setTimeout(() => {
      setActiveFires(prev => prev.filter(f => f.key !== key));
    }, FIRE_LIFETIME_MS);
  }, []);

  // Intro → Live
  useEffect(() => {
    const id = window.setTimeout(() => setPhase('live'), INTRO_DURATION_MS);
    return () => clearTimeout(id);
  }, []);

  // ────────────────────────────────────────────────────────────────
  // BURST: при входе Dashboard передаёт burstFires — массив
  // {nodeIdx, amount}. Они файрятся последовательно (0.6с между),
  // но ВИДИМЫ ОДНОВРЕМЕННО (каждый живёт 2с). После всех —
  // core pulse + balance tick с суммарным amount.
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!burstFires || burstFires.length === 0 || phase !== 'live') return;
    if (burstProcessedRef.current) return;
    burstProcessedRef.current = true;

    const timers: number[] = [];
    const schedule = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(fn, ms));
    };

    // Фаза 1: последовательные fires (1с между каждым).
    // Сначала VISUAL fire на ноде (ring + amount), через 0.8с — tick
    // счётчика + haptic. Юзер видит: ring → counter tick.
    burstFires.forEach((bf, i) => {
      const fireDelay = i * 1000; // 1с между fires (было 0.6с)

      // T+0: visual fire на ноде (ring + "+X.XXX")
      schedule(() => {
        addFire(bf.nodeIdx, bf.amount);
      }, fireDelay);

      // T+0.8s: counter tick + haptic (после ring анимации)
      schedule(() => {
        onFireRef.current?.(bf.amount, bf.nodeIdx);
      }, fireDelay + 800);
    });

    // Фаза 2: core pulse после всех fires
    const allFiresDone = burstFires.length * 1000;
    schedule(() => setCoreKey(k => k + 1), allFiresDone + 500);

    return () => timers.forEach(clearTimeout);
  }, [burstFires, phase, addFire]);

  // ────────────────────────────────────────────────────────────────
  // Серверный fire → steady-mode анимация (одиночный fire + cycle)
  // ────────────────────────────────────────────────────────────────
  const steadyTimersRef = useRef<number[]>([]);

  useEffect(() => {
    if (!serverFire || phase !== 'live') return;
    const amount = dripRef.current;
    if (amount <= 0) return; // нет контрактов — не файрим
    const idx = serverFire.nodeIdx;

    // Очищаем предыдущие таймеры steady цикла
    steadyTimersRef.current.forEach(clearTimeout);
    steadyTimersRef.current = [];
    const schedule = (fn: () => void, ms: number) => {
      steadyTimersRef.current.push(window.setTimeout(fn, ms));
    };

    // STAGE 1: fire
    addFire(idx, amount);

    // STAGE 2: core pulse (T=2.1s)
    schedule(() => setCoreKey(k => k + 1), 2100);

    // STAGE 3: balance tick (T=4.5s — быстрее чем раньше, т.к. огней мало)
    schedule(() => {
      onFireRef.current?.(amount, idx);
    }, 4500);
  }, [serverFire, phase, addFire]);

  // ────────────────────────────────────────────────────────────────
  // Background "neural network" — tiny line between two random nodes
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'live') return;
    let cancelled = false;
    let timeoutId: number | undefined;

    // Server-driven синхрон: tick каждые 2500мс, общий для всех клиентов.
    // В один момент времени все юзеры видят одну и ту же пару нод.
    const tick = () => {
      if (cancelled) return;
      const total = businesses.length;
      const tickId = currentTick(2500);
      const from = syncedRandomInt(tickId, 1, 0, total - 1);
      // Второй вариант seed'а (2) чтобы to не совпадал — если совпал, сдвигаем
      let to = syncedRandomInt(tickId, 2, 0, total - 1);
      if (to === from) to = (from + 1) % total;
      connectionKeyRef.current += 1;
      setConnection({ from, to, key: connectionKeyRef.current });
      // Следующий тик — ровно на границе 2500мс (синхрон между клиентами)
      const now = Date.now();
      const nextTickAt = (tickId + 1) * 2500;
      timeoutId = window.setTimeout(tick, Math.max(100, nextTickAt - now));
    };

    timeoutId = window.setTimeout(tick, 1200);
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [phase]);

  // ────────────────────────────────────────────────────────────────
  // selfDrive fallback — если нет ни server fire ни burst,
  // раз в ~60с файрит рандомную ноду
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'live') return;
    let cancelled = false;
    const timers: number[] = [];
    const schedule = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(() => { if (!cancelled) fn(); }, ms));
    };

    const selfDrive = () => {
      if (cancelled) return;
      // Пропускаем если: сервер активен, burst идёт, или dripAmount=0 (нет контрактов)
      if (serverFireRef.current || (burstFires && burstFires.length > 0 && !burstProcessedRef.current)) return;
      if (dripRef.current <= 0) return; // нет контрактов — не файрим

      // Синхронный выбор ноды: все юзеры в одном 60-сек окне видят
      // одну и ту же бизнес-ноду горящей (если у них есть контракты).
      const tickId = currentTick(60000);
      const idx = syncedRandomInt(tickId, 10, 0, businesses.length - 1);
      const amount = dripRef.current;

      addFire(idx, amount);
      schedule(() => setCoreKey(k => k + 1), 2100);
      schedule(() => {
        onFireRef.current?.(amount, idx);
      }, 4500);

      // Следующий selfDrive — на границе следующего 60-сек тика
      const nextAt = (tickId + 1) * 60000 + Math.floor(syncedRandom(tickId, 20) * 15000);
      schedule(selfDrive, Math.max(45000, nextAt - Date.now()));
    };

    // Стартуем selfDrive через 10с (после возможного burst)
    schedule(selfDrive, 10000);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [phase, burstFires, addFire]);

  // Pre-compute node positions
  const nodes = businesses.map((b, i) => {
    const angle = (i / businesses.length) * Math.PI * 2 - Math.PI / 2;
    return {
      ...b, i,
      cx: CENTER + Math.cos(angle) * RADIUS,
      cy: CENTER + Math.sin(angle) * RADIUS,
    };
  });

  return (
    <div className={cn('relative aspect-square w-full max-w-[300px] mx-auto', className)}>
      <svg viewBox="0 0 300 300" className="absolute inset-0 h-full w-full">
        <defs>
          <radialGradient id="core-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.85" />
            <stop offset="40%" stopColor="var(--accent-primary)" stopOpacity="0.25" />
            <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="orbit-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.4" />
            <stop offset="50%" stopColor="var(--accent-secondary)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="var(--accent-tertiary)" stopOpacity="0.4" />
          </linearGradient>
        </defs>

        {/* Core hit-target */}
        {onTapCenter && (
          <circle
            cx={CENTER} cy={CENTER} r="62"
            fill="transparent"
            style={{ cursor: 'pointer', pointerEvents: 'all' }}
            onClick={(e) => { e.stopPropagation(); onTapCenter(); }}
          />
        )}

        {/* Core glow + ripple system */}
        <g
          key={coreKey}
          style={{ transformOrigin: `${CENTER}px ${CENTER}px`, pointerEvents: 'none' }}
        >
          <circle
            cx={CENTER} cy={CENTER} r="60"
            fill="url(#core-glow)"
            className={phase === 'live' ? 'node-core-main' : 'node-core-intro-main'}
            style={{
              transformOrigin: `${CENTER}px ${CENTER}px`,
              ...(phase === 'intro' ? { animationDelay: `${INTRO_START_DELAY_S}s` } : null),
            }}
          />
          {phase === 'live' && (
            <>
              {[1, 2, 3, 4].map(n => (
                <circle
                  key={n}
                  cx={CENTER} cy={CENTER} r="60"
                  fill="none" stroke="var(--accent-primary)" strokeWidth="1.2"
                  className={`node-core-ripple node-core-ripple-${n}`}
                  style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
                />
              ))}
            </>
          )}
        </g>

        {/* Intro genesis burst */}
        {phase === 'intro' && (
          <g style={{ pointerEvents: 'none' }}>
            <circle cx={CENTER} cy={CENTER} r="6" fill="var(--accent-primary)"
              className="node-intro-burst"
              style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
            />
            {[
              { stroke: 'var(--accent-primary)', w: 2, cls: 'node-intro-radial-1' },
              { stroke: 'var(--accent-secondary)', w: 1.6, cls: 'node-intro-radial-2' },
              { stroke: 'var(--accent-tertiary)', w: 1.3, cls: 'node-intro-radial-3' },
            ].map(r => (
              <circle key={r.cls} cx={CENTER} cy={CENTER} r="60"
                fill="none" stroke={r.stroke} strokeWidth={r.w}
                className={`node-intro-radial ${r.cls}`}
                style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
              />
            ))}
          </g>
        )}

        {/* Orbital rings */}
        <circle cx={CENTER} cy={CENTER} r="60" fill="none"
          stroke="url(#orbit-stroke)" strokeWidth="0.8"
          className={phase === 'intro' ? 'orbit-ring-fade' : ''}
          style={phase === 'intro' ? { animationDelay: `${INTRO_START_DELAY_S + 0.2}s` } : undefined}
        />
        <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none"
          stroke="url(#orbit-stroke)" strokeWidth="0.8" strokeDasharray="4 6"
          className={phase === 'intro' ? 'orbit-ring-fade' : ''}
          style={phase === 'intro' ? { animationDelay: `${INTRO_START_DELAY_S + 0.45}s` } : undefined}
        />
        <circle cx={CENTER} cy={CENTER} r="135" fill="none"
          stroke="url(#orbit-stroke)" strokeWidth="0.8" strokeDasharray="2 8"
          className={phase === 'intro' ? 'orbit-ring-fade' : ''}
          style={phase === 'intro' ? { animationDelay: `${INTRO_START_DELAY_S + 0.7}s` } : undefined}
        />

        {/* Neural network connections */}
        {phase === 'live' && connection && (
          <line
            key={`conn-${connection.key}`}
            x1={nodes[connection.from].cx} y1={nodes[connection.from].cy}
            x2={nodes[connection.to].cx} y2={nodes[connection.to].cy}
            stroke="var(--accent-secondary)" strokeWidth="0.65" strokeLinecap="round"
            className="node-connection"
          />
        )}

        {/* Drift particles */}
        {phase === 'live' && (
          <>
            <circle cx={CENTER} cy={CENTER} r="1.4" fill="var(--accent-primary)" className="drift-particle drift-particle-1" />
            <circle cx={CENTER} cy={CENTER} r="1.2" fill="var(--accent-secondary)" className="drift-particle drift-particle-2" />
            <circle cx={CENTER} cy={CENTER} r="1.5" fill="var(--accent-tertiary)" className="drift-particle drift-particle-3" />
            <circle cx={CENTER} cy={CENTER} r="1.1" fill="var(--accent-secondary)" className="drift-particle drift-particle-4" />
            <circle cx={CENTER} cy={CENTER} r="1.3" fill="var(--accent-primary)" className="drift-particle drift-particle-5" />
          </>
        )}

        {/* 7 business nodes */}
        {nodes.map((b) => {
          // Все активные fires для этой ноды
          const nodeFires = activeFires.filter(f => f.idx === b.i);
          const hasActiveFire = nodeFires.length > 0;
          const introDelay = INTRO_START_DELAY_S + 0.95 + b.i * 0.14;

          const liveClass = hasActiveFire && phase === 'live' ? 'node-fire-pulse' : 'node-pulse';
          const circleKey = hasActiveFire
            ? `${b.short}-fire-${nodeFires[nodeFires.length - 1]?.key}`
            : b.short;

          return (
            <g
              key={b.short}
              onClick={onTapNode ? (e) => {
                e.stopPropagation();
                sounds.nodeTap();
                onTapNode(b.i);
              } : undefined}
              style={onTapNode ? { cursor: 'pointer' } : undefined}
            >
              {onTapNode && (
                <circle cx={b.cx} cy={b.cy} r="20" fill="transparent"
                  style={{ pointerEvents: 'all' }}
                />
              )}

              <circle
                key={circleKey}
                cx={b.cx} cy={b.cy} r="14"
                fill={`hsl(${b.hue} 70% 60% / 0.18)`}
                stroke={`hsl(${b.hue} 80% 65%)`}
                strokeWidth="1.4"
                className={phase === 'intro' ? 'node-intro-pop' : liveClass}
                style={{
                  transformOrigin: `${b.cx}px ${b.cy}px`,
                  animationDelay: phase === 'intro' ? `${introDelay}s` : `${b.i * 0.55}s`,
                  pointerEvents: 'none',
                }}
              />

              <image
                href={b.icon}
                x={b.cx - 11} y={b.cy - 11}
                width="22" height="22"
                preserveAspectRatio="xMidYMid meet"
                pointerEvents="none"
                className={phase === 'intro' ? 'node-intro-label' : ''}
                style={phase === 'intro' ? { animationDelay: `${introDelay + 0.3}s` } : undefined}
              />

              {/* Concurrent fire rings — каждый с уникальным key */}
              {nodeFires.map(f => (
                <circle
                  key={`ring-${f.key}`}
                  cx={b.cx} cy={b.cy} r="14"
                  fill="none"
                  stroke={`hsl(${b.hue} 75% 65% / 0.7)`}
                  strokeWidth="1.1"
                  className="node-fire-ring"
                  style={{ transformOrigin: `${b.cx}px ${b.cy}px` }}
                />
              ))}

              {/* Concurrent floating amounts — всегда 3 знака после точки.
                  Что видит юзер на ноде = столько прибавляется в счётчик. */}
              {nodeFires.filter(f => f.amount > 0.0001).map(f => (
                <text
                  key={`amt-${f.key}`}
                  x={b.cx} y={b.cy - 20}
                  textAnchor="middle"
                  fontSize="9" fontWeight="600"
                  fontFamily="Jura, sans-serif"
                  fill="var(--text-secondary)"
                  className="node-fire-amount"
                  style={{ transformOrigin: `${b.cx}px ${b.cy - 20}px` }}
                >
                  +{f.amount.toFixed(3)}
                </text>
              ))}
            </g>
          );
        })}

        {/* Center LPS readout */}
        <g
          className={phase === 'intro' ? 'node-text-intro' : ''}
          style={phase === 'intro' ? {
            transformOrigin: `${CENTER}px ${CENTER}px`,
            animationDelay: `${INTRO_START_DELAY_S + 1.9}s`,
          } : undefined}
        >
          <text x={CENTER} y={CENTER - 18} textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fontWeight="500" fontFamily="Jura, sans-serif"
            fill="var(--text-muted)" letterSpacing="0.18em">
            NODES
          </text>
          <text x={CENTER} y={CENTER + 2} textAnchor="middle" dominantBaseline="middle"
            fontSize="26" fontWeight="800" fontFamily="Jura, sans-serif"
            fill="var(--text-primary)" letterSpacing="0.02em">
            {totalLps.toLocaleString('ru-RU')}
          </text>
          <text x={CENTER} y={CENTER + 22} textAnchor="middle" dominantBaseline="middle"
            fontSize="8" fontWeight="500" fontFamily="Jura, sans-serif"
            fill="var(--text-muted)" letterSpacing="0.18em">
            LPS · ONLINE
          </text>
        </g>
      </svg>
    </div>
  );
}

export const NodeOrbit = memo(NodeOrbitImpl);
